/**
 * Bulk Import — main orchestrator component.
 * State machine: idle → previewing → review → submitting → submitted
 */

import { requestFailure, type FormFailure } from '../save-failure-words';
import { importFailureOf } from '../../../api/admin/import/failures';
import { useState } from 'react';
import { Button } from '../../../components/button';
import { useTranslation } from 'react-i18next';
import { CommonHeader } from '../../shell/common-header';
import { BulkImportUpload } from './bulk-import-upload';
import { BulkImportPreview } from './bulk-import-preview';
import { BulkImportSubmitting } from './bulk-import-submitting';
import { ImportPlanResponse, ImportExecuteResponse } from '../../../api/admin/import/types';
import { useImportExecute, useImportPreview } from '../../../api/admin/import/hooks';
import { FormMessage } from '../../../components/form-field';
import bulk from './bulk-import.module.css';

type BulkImportStep = 'idle' | 'previewing' | 'review' | 'submitting' | 'submitted';

interface Props {
  onBack?: () => void;
  onOpenHistory: (jobId?: string) => void;
  /** When true, renders only the inner content — no portal-container/CommonHeader wrapper */
  embedded?: boolean;
}

export function BulkImport({ onBack, onOpenHistory, embedded = false }: Props) {
  const { t } = useTranslation();
  const [step, setStep] = useState<BulkImportStep>('idle');
  const [fileName, setFileName] = useState('');
  const [parsedFile, setParsedFile] = useState<object | null>(null);
  const [plan, setPlan] = useState<ImportPlanResponse | null>(null);
  const [previewError, setPreviewError] = useState<FormFailure | null>(null);
  const [executeResult, setExecuteResult] = useState<ImportExecuteResponse | null>(null);
  const preview = useImportPreview();
  const execute = useImportExecute();

  async function handlePreview(parsed: object, name: string) {
    setParsedFile(parsed);
    setFileName(name);
    setPreviewError(null);
    setStep('previewing');
    try {
      const result = await preview.mutateAsync(parsed);
      setPlan(result);
      setStep('review');
    } catch (err: unknown) {
      // The API answers a rejected preview with the plan attached, so the
      // screen can still show what *would* have happened. `unknown` plus one
      // narrowing keeps that contract explicit instead of implied by `any`.
      const failure = importFailureOf(err);
      if (failure.plan) {
        setPlan(failure.plan);
        setStep('review');
      } else {
        setPreviewError(requestFailure(t, err, 'preview'));
        setStep('idle');
      }
    }
  }

  async function handleExecute() {
    if (!parsedFile || !plan) return;
    setStep('submitting');
    try {
      const result = await execute.mutateAsync({ file: parsedFile, expectedSummary: plan.summary });
      setExecuteResult(result);
      setStep('submitted');
    } catch (err: unknown) {
      const failure = importFailureOf(err);
      if (failure.summaryChanged) {
        setPreviewError({ detail: t('admin.bulkImport.expectedSummaryMismatch') });
        setPlan(null);
        setStep('idle');
      } else if (failure.plan) {
        setPlan(failure.plan);
        setStep('review');
      } else {
        setPreviewError(requestFailure(t, err, 'execute'));
        setStep('idle');
      }
    }
  }

  function handleChangeFile() {
    setPlan(null);
    setParsedFile(null);
    setFileName('');
    setPreviewError(null);
    setStep('idle');
  }

  const content = (
    <>
      {step === 'idle' || step === 'previewing' ? (
        <>
          {previewError && <FormMessage title={previewError.title}>{previewError.detail}</FormMessage>}
          <BulkImportUpload onPreview={handlePreview} isLoading={step === 'previewing'} />
        </>
      ) : step === 'review' && plan ? (
        <BulkImportPreview
          plan={plan}
          fileName={fileName}
          onChangefile={handleChangeFile}
          onExecute={handleExecute}
          isExecuting={false}
        />
      ) : step === 'submitting' ? (
        <BulkImportSubmitting onOpenHistory={() => onOpenHistory()} />
      ) : step === 'submitted' && executeResult ? (
        <SubmittedView result={executeResult} onOpenHistory={() => onOpenHistory(executeResult.jobId)} onStartOver={handleChangeFile} />
      ) : null}
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="portal-shell portal-container">
      <CommonHeader title={t('admin.bulkImport.title')} showBackButton onBackClick={onBack} />
      <main className="portal-main">
        <div className={`portal-content ${bulk.bulkImportContainer}`}>
          {content}
        </div>
      </main>
    </div>
  );
}

function SubmittedView({
  result,
  onOpenHistory,
  onStartOver,
}: {
  result: ImportExecuteResponse;
  onOpenHistory: () => void;
  onStartOver: () => void;
}) {
  const { t } = useTranslation();
  const isOk = result.finalStatus === 'completed';
  return (
    <div className="text-center py-8">
      <div className="text-40 mb-3">{isOk ? '✅' : '⚠️'}</div>
      <h3>{t('admin.bulkImport.submitted.title')}</h3>
      <p>{t('admin.bulkImport.submitted.summary', { applied: result.appliedOps, failed: result.failedOps })}</p>
      <div className="flex gap-2.5 justify-center mt-5">
        <Button type="button" variant="gold" onClick={onOpenHistory}>
          {t('admin.bulkImport.submitted.viewInHistory')}
        </Button>
        <Button type="button" variant="gold" onClick={onStartOver}>
          {t('admin.bulkImport.submitted.startOver')}
        </Button>
      </div>
    </div>
  );
}
